import React, { useMemo, useState } from 'react';
import { StepProps, SPECIALTY_COLORS } from '../../types';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, MessageCircle, BarChart3, Check } from 'lucide-react';
import { playSuccess, playWrong } from '../../utils/sounds';
import { Celebration } from '../Celebration';
import { shuffle } from '../../utils/shuffle';
interface Feature {
  label: string;
  score: number;
  detail: string;
}
const FEATURES: Record<string, Feature[]> = {
  Nephrologist: [{
    label: 'Long-term safety profile',
    score: 92,
    detail: "Top priority — they need confidence it won't harm fragile patients"
  }, {
    label: 'Kidney-specific outcome data',
    score: 88,
    detail: 'eGFR slope, UACR reduction — the metrics they track daily'
  }, {
    label: 'Treatment sequencing guidance',
    score: 82,
    detail: 'Where it fits in their existing protocol matters enormously'
  }, {
    label: 'Hyperkalaemia management data',
    score: 78,
    detail: 'Directly addresses their #1 safety concern'
  }, {
    label: 'Real-world evidence',
    score: 65,
    detail: 'Peer validation from similar patient populations'
  }, {
    label: 'CV outcome benefit',
    score: 55,
    detail: 'Valued but secondary to kidney-specific endpoints'
  }, {
    label: 'Ease of prescribing',
    score: 40,
    detail: 'Less important — they manage complex regimens already'
  }, {
    label: 'Cost-effectiveness data',
    score: 35,
    detail: 'Relevant but rarely the deciding factor'
  }],
  Cardiologist: [{
    label: 'CV outcome data (hard endpoints)',
    score: 95,
    detail: 'MACE reduction, HF hospitalisation — non-negotiable'
  }, {
    label: 'Dual organ protection evidence',
    score: 88,
    detail: 'Heart + kidney protection in one decision appeals strongly'
  }, {
    label: 'NNT and absolute risk reduction',
    score: 82,
    detail: 'They think in numbers — make the case quantitatively'
  }, {
    label: 'Combination therapy compatibility',
    score: 78,
    detail: 'Fits with their rapid multi-modal approach'
  }, {
    label: 'Early intervention benefit',
    score: 72,
    detail: 'Aligns with their proactive mindset'
  }, {
    label: 'Guideline positioning',
    score: 60,
    detail: 'Helpful but they often act ahead of guidelines'
  }, {
    label: 'Safety profile',
    score: 45,
    detail: "Less of a concern — they're comfortable with risk trade-offs"
  }, {
    label: 'Patient adherence data',
    score: 38,
    detail: 'Lower priority in their decision framework'
  }],
  Endocrinologist: [{
    label: 'Biomarker evidence (UACR, eGFR)',
    score: 90,
    detail: 'Data-driven specialty — they want measurable proof'
  }, {
    label: 'Metabolic pathway alignment',
    score: 85,
    detail: "How it fits with SGLT2/GLP1 they're already prescribing"
  }, {
    label: 'Clear escalation triggers',
    score: 82,
    detail: 'When exactly to add this — removes the ambiguity they dislike'
  }, {
    label: 'Guideline recommendations',
    score: 80,
    detail: 'Strong guideline backing gives them confidence to act'
  }, {
    label: 'Preventative benefit framing',
    score: 72,
    detail: 'Aligns with their preventative mindset'
  }, {
    label: 'Non-glycaemic benefit data',
    score: 65,
    detail: 'Important but this is where they hesitate to "overstep"'
  }, {
    label: 'Peer prescribing patterns',
    score: 50,
    detail: 'Moderately influential — they prefer data over social proof'
  }, {
    label: 'Simplicity of monitoring',
    score: 42,
    detail: 'They already monitor extensively — less of a differentiator'
  }],
  PCP: [{
    label: 'Simple prescribing pathway',
    score: 92,
    detail: 'Complexity is the enemy — make it easy to start'
  }, {
    label: 'Guideline endorsement',
    score: 90,
    detail: 'They need authoritative backing before acting'
  }, {
    label: 'Peer adoption evidence',
    score: 85,
    detail: '"Other PCPs are doing this" is highly motivating'
  }, {
    label: 'Patient identification criteria',
    score: 80,
    detail: 'Clear "who to treat" criteria reduces decision anxiety'
  }, {
    label: 'Safety and tolerability',
    score: 75,
    detail: 'Important for confidence in a generalist setting'
  }, {
    label: 'Prevention narrative',
    score: 68,
    detail: 'Aligns with their holistic, preventative approach'
  }, {
    label: 'Specialist referral reduction',
    score: 55,
    detail: 'Appealing but not a primary motivator'
  }, {
    label: 'Complex outcome data',
    score: 30,
    detail: 'Too detailed — they prefer simple, clear messages'
  }]
};
export function FeatureSelection({
  state,
  nextStep
}: StepProps) {
  const role = state.selectedSpecialty || 'Nephrologist';
  const color = SPECIALTY_COLORS[role];
  const features = useMemo(() => shuffle(FEATURES[role]), [role]);
  const [selected, setSelected] = useState<string[]>([]);
  const [revealed, setRevealed] = useState(false);
  const [celebration, setCelebration] = useState<'success' | 'wrong' | null>(null);
  const toggleFeature = (label: string) => {
    if (revealed) return;
    if (selected.includes(label)) {
      setSelected(selected.filter(s => s !== label));
    } else if (selected.length < 3) {
      setSelected([...selected, label]);
    }
  };
  const handleReveal = () => {
    setRevealed(true);
    const matches = selected.filter(s => top3Labels.includes(s)).length;
    if (matches >= 2) {
      playSuccess();
      setCelebration('success');
    } else {
      playWrong();
      setCelebration('wrong');
    }
    setTimeout(() => setCelebration(null), 2000);
  };
  // Sort features by score for reveal
  const sortedFeatures = [...features].sort((a, b) => b.score - a.score);
  const top3Labels = sortedFeatures.slice(0, 3).map(f => f.label);
  const getMatchCount = () => selected.filter(s => top3Labels.includes(s)).length;
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto pt-20" data-id="element-408">
      <Celebration type={celebration} color={color} data-id="element-409" />
      <div className="max-w-4xl w-full mx-auto" data-id="element-410">
        <div className="text-center mb-8" data-id="element-411">
          <div className="inline-flex items-center justify-center p-3 rounded-full mb-4" style={{
          backgroundColor: `${color}15`,
          color
        }} data-id="element-412">
            <BarChart3 size={28} data-id="element-413" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-dark mb-3" data-id="element-414">
            Which features & arguments would you lead with?
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-415">
            Based on what you've learned about {role}s, pick the{' '}
            <strong data-id="element-416">top 3</strong> features or arguments your group would
            prioritise when engaging them.
          </p>
        </div>

        {!revealed ? <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8" data-id="element-417">
              {features.map(feature => {
            const isSelected = selected.includes(feature.label);
            const selectionIndex = selected.indexOf(feature.label);
            return <motion.button key={feature.label} whileHover={{
              scale: 1.01
            }} whileTap={{
              scale: 0.99
            }} onClick={() => toggleFeature(feature.label)} className={`p-4 rounded-xl border-2 text-left flex items-start transition-all duration-200 ${isSelected ? 'bg-white shadow-sm' : selected.length >= 3 ? 'bg-gray-50 border-gray-100 opacity-50 cursor-not-allowed' : 'bg-white border-gray-200 hover:border-gray-300'}`} style={{
              borderColor: isSelected ? color : undefined
            }} disabled={!isSelected && selected.length >= 3} data-id="element-418">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 text-sm font-bold transition-colors ${isSelected ? 'text-white' : 'bg-gray-100 text-gray-400'}`} style={{
                backgroundColor: isSelected ? color : undefined
              }} data-id="element-419">
                      {isSelected ? selectionIndex + 1 : ''}
                    </div>
                    <span className={`font-medium leading-snug ${isSelected ? 'text-dark' : 'text-gray-700'}`} data-id="element-420">
                      {feature.label}
                    </span>
                  </motion.button>;
          })}
            </div>

            <div className="flex justify-center h-16" data-id="element-421">
              {selected.length === 3 && <motion.button initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} onClick={handleReveal} className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white rounded-full transition-colors shadow-lg" style={{
            backgroundColor: color
          }} data-id="element-422">
                  See What the Data Shows
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-423" />
                </motion.button>}
              {selected.length < 3 && <p className="text-gray-400 text-sm self-center" data-id="element-424">
                  Select {3 - selected.length} more feature
                  {3 - selected.length !== 1 ? 's' : ''}
                </p>}
            </div>
          </> : <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} data-id="element-425">
            {/* Score banner */}
            <div className={`rounded-2xl p-5 mb-8 text-center ${getMatchCount() === 3 ? 'bg-green-50 border border-green-200' : getMatchCount() >= 1 ? 'bg-amber-50 border border-amber-200' : 'bg-red-50 border border-red-200'}`} data-id="element-426">
              <p className={`text-lg font-bold ${getMatchCount() === 3 ? 'text-green-800' : getMatchCount() >= 1 ? 'text-amber-800' : 'text-red-800'}`} data-id="element-427">
                Your group matched {getMatchCount()} of 3 top-performing
                features
              </p>
            </div>

            {/* Ranked results */}
            <div className="bg-white rounded-3xl border border-gray-200 p-8 mb-8" data-id="element-428">
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-6" data-id="element-429">
                Feature Effectiveness — Observed Ranking for {role}s
              </h3>
              <div className="space-y-5" data-id="element-430">
                {sortedFeatures.map((feature, i) => {
              const wasSelected = selected.includes(feature.label);
              const isTop3 = i < 3;
              return <motion.div key={feature.label} initial={{
                opacity: 0,
                x: 30
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.4,
                delay: i * 0.1
              }} data-id="element-431">
                      <div className="flex items-center justify-between mb-1.5" data-id="element-432">
                        <div className="flex items-center" data-id="element-433">
                          <span className={`w-7 h-7 rounded-lg text-sm font-bold flex items-center justify-center mr-3 ${isTop3 ? 'text-white' : 'bg-gray-100 text-gray-400'}`} style={{
                      backgroundColor: isTop3 ? color : undefined
                    }} data-id="element-434">
                            {i + 1}
                          </span>
                          <span className={`font-semibold ${isTop3 ? 'text-dark' : 'text-gray-500'}`} data-id="element-435">
                            {feature.label}
                          </span>
                          {wasSelected && <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full ${isTop3 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-436">
                              {isTop3 ? '✓ You picked this' : 'You picked this'}
                            </span>}
                        </div>
                        <span className="text-sm font-bold tabular-nums" style={{
                    color: isTop3 ? color : '#9ca3af'
                  }} data-id="element-437">
                          {feature.score}%
                        </span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden ml-10" data-id="element-438">
                        <motion.div className="h-full rounded-full" style={{
                    backgroundColor: isTop3 ? color : '#d1d5db',
                    opacity: isTop3 ? 1 - i * 0.15 : 0.5
                  }} initial={{
                    width: 0
                  }} animate={{
                    width: `${feature.score}%`
                  }} transition={{
                    duration: 0.8,
                    delay: 0.3 + i * 0.1
                  }} data-id="element-439" />
                      </div>
                      {isTop3 && <motion.p initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 0.8 + i * 0.1
                }} className="text-sm text-gray-500 ml-10 mt-1 italic" data-id="element-440">
                          {feature.detail}
                        </motion.p>}
                    </motion.div>;
            })}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 mb-8 flex items-start" data-id="element-441">
              <MessageCircle className="text-blue-500 mr-4 mt-1 flex-shrink-0" size={24} data-id="element-442" />
              <div data-id="element-443">
                <h4 className="text-blue-900 font-bold mb-2" data-id="element-444">
                  Discuss with your group:
                </h4>
                <ul className="text-blue-800 space-y-1.5" data-id="element-445">
                  <li data-id="element-446">
                    Were you surprised by the top features? What would you
                    change in your current approach?
                  </li>
                  <li data-id="element-447">
                    How would you translate the #1 feature into a specific
                    conversation opener?
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex justify-center pb-8" data-id="element-448">
              <button onClick={nextStep} className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors" data-id="element-449">
                Now Pick the Right Framing
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-450" />
              </button>
            </div>
          </motion.div>}
      </div>
    </div>;
}